import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CACHE_TIME, STALE_TIME } from "../ApiProvider";
import { api } from "./api";
import { USE_RETRIEVE_DIVISION, USE_RETRIEVE_DIVISIONS_BY_SEASON_ID, } from "./DivisionApiProvider";
const USE_RETRIEVE_SEASONS_BY_LEAGUE_ID = "useRetrieveSeasonsByLeagueId";
const USE_RETRIEVE_SEASON = "useRetrieveSeason";
export const useRetrieveSeasonsByLeagueId = ({ leagueId, enabled = true, }) => {
    return useQuery({
        queryKey: [USE_RETRIEVE_SEASONS_BY_LEAGUE_ID, leagueId],
        queryFn: async () => {
            const { data } = await api.get(`/leagues/${leagueId}/seasons/`);
            return data;
        },
        enabled: !!leagueId && enabled,
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
    });
};
export const useRetrieveSeason = ({ id, enabled = true, }) => {
    return useQuery({
        queryKey: [USE_RETRIEVE_SEASON, id],
        queryFn: async () => {
            const { data } = await api.get(`/seasons/${id}/`);
            return data;
        },
        enabled: !!id && enabled,
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
    });
};
export const useCreateSeason = ({ onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data) => {
            return api.post(`/seasons/`, data);
        },
        onMutate,
        onSuccess: (data) => {
            const leagueId = data.data
                .league;
            queryClient.invalidateQueries([
                USE_RETRIEVE_SEASONS_BY_LEAGUE_ID,
                leagueId,
            ]);
            onSuccess(data);
        },
        onError,
    });
};
export const useUpdateSeason = ({ onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data) => {
            const id = data.id;
            return api.put(`/seasons/${id}/`, data);
        },
        onMutate,
        onSuccess: (data) => {
            const season = data.data;
            const leagueId = season.league;
            queryClient.invalidateQueries([
                USE_RETRIEVE_SEASONS_BY_LEAGUE_ID,
                leagueId,
            ]);
            queryClient.invalidateQueries([USE_RETRIEVE_SEASON, season.id]);
            if (onSuccess)
                onSuccess(data);
        },
        onError,
    });
};
export const useDeleteSeason = ({ onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, leagueId }) => {
            return api.delete(`/seasons/${id}/`).then(() => ({ id, leagueId }));
        },
        onMutate: (vars) => onMutate && onMutate(vars.id),
        onSuccess: (_res, vars) => {
            queryClient.invalidateQueries([
                USE_RETRIEVE_SEASONS_BY_LEAGUE_ID,
                vars.leagueId,
            ]);
            queryClient.removeQueries([USE_RETRIEVE_SEASON, vars.id]);
            if (onSuccess)
                onSuccess();
        },
        onError,
    });
};
export const useCancelSeason = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id }) => {
            return api.post(`/seasons/${id}/cancel/`);
        },
        onSuccess: (data) => {
            const season = data.data;
            const leagueId = season.league;
            queryClient.invalidateQueries([USE_RETRIEVE_SEASON, season.id]);
            queryClient.invalidateQueries([
                USE_RETRIEVE_SEASONS_BY_LEAGUE_ID,
                leagueId,
            ]);
        },
    });
};
export const useAssignTeamsToDivisions = ({ seasonId, onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data) => {
            return api.patch(`/seasons/${seasonId}/assign-teams/`, data);
        },
        onMutate,
        onSuccess: (data) => {
            // Invalidate season data to refresh divisions and their teams
            queryClient.invalidateQueries([USE_RETRIEVE_SEASON, seasonId]);
            // Also invalidate any division-specific queries if they exist
            queryClient.invalidateQueries([USE_RETRIEVE_DIVISION]);
            queryClient.invalidateQueries([
                USE_RETRIEVE_DIVISIONS_BY_SEASON_ID,
                seasonId,
            ]);
            if (onSuccess)
                onSuccess(data);
        },
        onError,
    });
};
// Season Config hooks
const USE_RETRIEVE_SEASON_CONFIG = "useRetrieveSeasonConfig";
const USE_RETRIEVE_SEASON_MATCHES = "useRetrieveSeasonMatches";
const USE_RETRIEVE_LEAGUE_MATCH = "useRetrieveLeagueMatch";
export const useRetrieveSeasonMatches = ({ seasonId, divisionId, enabled = true, }) => {
    return useQuery({
        queryKey: [USE_RETRIEVE_SEASON_MATCHES, seasonId, divisionId],
        queryFn: async () => {
            const params = divisionId ? `?division=${divisionId}` : "";
            const { data } = await api.get(`/seasons/${seasonId}/matches/${params}`);
            return data;
        },
        enabled: !!seasonId && enabled,
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
    });
};
export const useRetrieveSeasonConfig = ({ seasonId, enabled = true, }) => {
    return useQuery({
        queryKey: [USE_RETRIEVE_SEASON_CONFIG, seasonId],
        queryFn: async () => {
            const { data } = await api.get(`/seasons/${seasonId}/config/`);
            return data;
        },
        enabled: !!seasonId && enabled,
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
    });
};
export const useUpdateSeasonConfig = ({ seasonId, onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data) => {
            return api.patch(`/seasons/${seasonId}/config/`, data);
        },
        onMutate,
        onSuccess: (data) => {
            queryClient.invalidateQueries([USE_RETRIEVE_SEASON_CONFIG, seasonId]);
            if (onSuccess)
                onSuccess(data);
        },
        onError,
    });
};
export const useGenerateFixtures = ({ seasonId, onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async () => {
            return api.post(`/seasons/${seasonId}/generate/`);
        },
        onMutate,
        onSuccess: (data) => {
            // Invalidate season and config to reflect updated status
            queryClient.invalidateQueries([USE_RETRIEVE_SEASON, seasonId]);
            queryClient.invalidateQueries([USE_RETRIEVE_SEASON_CONFIG, seasonId]);
            // Also invalidate the seasons list (status changed to active)
            queryClient.invalidateQueries([USE_RETRIEVE_SEASONS_BY_LEAGUE_ID]);
            if (onSuccess)
                onSuccess(data);
        },
        onError,
    });
};
export const useRetrieveLeagueMatch = ({ id, enabled = true, }) => {
    return useQuery({
        queryKey: [USE_RETRIEVE_LEAGUE_MATCH, id],
        queryFn: async () => {
            const { data } = await api.get(`/league-matches/${id}/`);
            return data;
        },
        enabled: !!id && enabled,
        staleTime: STALE_TIME,
        cacheTime: CACHE_TIME,
    });
};
export const useCreateLeagueMatch = ({ onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data) => {
            return api.post(`/league-matches/`, data);
        },
        onMutate,
        onSuccess: (data) => {
            queryClient.invalidateQueries([USE_RETRIEVE_SEASON_MATCHES]);
            if (onSuccess)
                onSuccess(data);
        },
        onError,
    });
};
export const useBulkCreateLeagueMatches = ({ onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (fixtures) => {
            return api.post(`/league-matches/bulk-create/`, fixtures);
        },
        onMutate,
        onSuccess: (data) => {
            queryClient.invalidateQueries([USE_RETRIEVE_SEASON_MATCHES]);
            if (onSuccess)
                onSuccess(data);
        },
        onError,
    });
};
export const useUpdateLeagueMatch = ({ onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data) => {
            return api.put(`/league-matches/${data.id}/`, data);
        },
        onMutate,
        onSuccess: (data) => {
            const match = data.data;
            queryClient.invalidateQueries([USE_RETRIEVE_LEAGUE_MATCH, match.id]);
            queryClient.invalidateQueries([USE_RETRIEVE_SEASON_MATCHES]);
            if (onSuccess)
                onSuccess(data);
        },
        onError,
    });
};
export const useDeleteLeagueMatch = ({ onMutate, onSuccess, onError, }) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id) => {
            return api.delete(`/league-matches/${id}/`);
        },
        onMutate,
        onSuccess: () => {
            queryClient.invalidateQueries([USE_RETRIEVE_SEASON_MATCHES]);
            if (onSuccess)
                onSuccess();
        },
        onError,
    });
};
export const useCreateLeagueMatchResult = (leagueMatch, onMutate, onSuccess, onError) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data) => {
            return api.post(`/league-match-results/`, data);
        },
        onMutate,
        onSuccess: () => {
            queryClient.invalidateQueries([
                USE_RETRIEVE_LEAGUE_MATCH,
                leagueMatch?.id,
            ]);
            queryClient.invalidateQueries([USE_RETRIEVE_SEASON_MATCHES]);
            onSuccess();
        },
        onError,
    });
};
export const useUpdateLeagueMatchResult = (leagueMatch, onMutate, onSuccess, onError) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data) => {
            const id = leagueMatch?.leagueMatchResult?.id;
            return api.put(`/league-match-results/${id}/`, data);
        },
        onMutate,
        onSuccess: () => {
            queryClient.invalidateQueries([
                USE_RETRIEVE_LEAGUE_MATCH,
                leagueMatch?.id,
            ]);
            queryClient.invalidateQueries([USE_RETRIEVE_SEASON_MATCHES]);
            onSuccess();
        },
        onError,
    });
};
export const useBulkUpdatePlayerLeagueMatchStats = (leagueMatch, onMutate, onSuccess, onError) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data) => {
            return api.put(`/bulk/player-league-match-stats/update/`, { updates: data });
        },
        onMutate,
        onSuccess: () => {
            queryClient.invalidateQueries([
                USE_RETRIEVE_LEAGUE_MATCH,
                leagueMatch?.id,
            ]);
            queryClient.invalidateQueries([USE_RETRIEVE_SEASON_MATCHES]);
            onSuccess();
        },
        onError,
    });
};
